import {Injectable, OnDestroy} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {BehaviorSubject, combineLatest, Observable, of, Subject, UnaryFunction} from 'rxjs';
import {filter, map, takeUntil} from 'rxjs/operators';
import {FieldUiDataObj, FieldValidationObj, UiDataObj} from '../../../../generated/generated-core.model';
import {CopyPasteConfig} from '../../../shared/interfaces/copy-paste-config';
import {MxButtonModel} from '../../mx-button/components/mx-button.model';
import {MxFormAndFieldsObj} from '../components/mx-form/mx-form-and-fields.obj';
import {MxFieldConfig} from '../modules/mx-dynamic-fields/services/mx-field-customizer.service';
import {MxDynamicFormService} from './mx-dynamic-form.service';


export type MxFormMode = 'new' | 'edit' | 'readonly' | 'duplicate';

export interface OnBoFormMetadataReady {
    boOnMetadataReady(metaData: ObjectMetaData): ObjectMetaData;
}

export interface OnBoFormReady {
    boOnFormReady(form: FormGroup): void;
}

export interface OnBoFormBeforeSubmit {
    boOnBeforeSubmit(form: FormGroup): void;
}

export class FormActionConfig {
    text: string | (() => string);
    visible: boolean = true;
    disableFunction: () => boolean;
}

export class FormActionsConfig {
    submit: Partial<FormActionConfig>;
    cancel: Partial<FormActionConfig>;
}

export class FormTabsConfig {
    url: string;
    name: string;
}

/**
 * showNavigation. Show '<-' near title for comeback to previous component
 * addPrefixToTitle. Remove or add ['ADD','Edit'] to form title. By default, this setting is true
 */
export class FormPropsConfig {
    showNavigation?: boolean;
    navigateBackOnSave?: boolean;
    navigateBackOnCancel?: boolean;
    addPrefixToTitle?: boolean;
    submitFunction?: SubmitFn;
    notifyOnSaveFn?: NotifyOnSaveFn;
    closeOnSubmit?: boolean;
    showErrorOnSubmitInDialog?: boolean;
    returnResponseAfterSaveInDialog?: boolean;
    showErrorMessage?: boolean;
    informationKey?: string;
    customNavigation?: {
        onCancel?: () => void;
        onBack?: () => void;
        onSubmitRedirect?: (response?: any) => void;
    };
}

export class FormMetaAndInitialData {
    objectMetaSource: ObjectMetaData | null;
    initialData: any;
}

export class ObjectMetaData {
    asyncValidation?: boolean;
    describeOfObj?: string;
    fieldsData: ReadonlyArray<Readonly<FieldUiDataObj>>;

    public static modifyFields(meta: ObjectMetaData,
                                  modification: (fieldUiDataObj: FieldUiDataObj) => FieldUiDataObj): ObjectMetaData {
        meta.fieldsData = meta.fieldsData.map(value => modification({...value}));
        return meta;
    }
}

/**
 * onSubmitComplete restores the form state that is disabled while submission is in progress.
 * Custom submit functions that keep the form open have to call it once the submission is finished.
 */
export type SubmitFn = (form: FormGroup, formMode: MxFormMode, url: string, onSubmitComplete?: () => void) => void;

export type NotifyOnSaveFn = (saveOpWithPipe: Observable<any>, action:  'update' | 'create') => Observable<any>;

export class PageActions {
    name: string;
    permission?: string;
    action: () => void;
}

export class FieldsDataConfig {
    className?: string;
    useUiObjectCache?: false;
    force?: boolean;
    metaObject?: ObjectMetaData;
    valueObject?: object;
    metaObservable?: Observable<ObjectMetaData>;
    valueObservable?: Observable<any>;
}

export class ObjectMetaDataBuilder {
    meta: ObjectMetaData;
    fieldsData: Array<FieldUiDataObj> = [];

    constructor() {
        this.meta = new ObjectMetaData();
        this.meta.describeOfObj = 'Object';
        this.meta.asyncValidation = false;
    }
    public addExistingField(field: FieldUiDataObj): ObjectMetaDataBuilder {
        this.fieldsData.push(field);
        return this;
    }

    public addField(name: string, type: string, label?: string, validation?: FieldValidationObj): ObjectMetaDataBuilder {
        let field: FieldUiDataObj = new FieldUiDataObj();
        field.type = type;
        field.name = name;
        field.label = label || name;
        if (validation) {
            field.validation = validation;
        } else {
            field.validation = new FieldValidationObj();
        }
        this.fieldsData.push(field);
        return this;
    }

    public build(): ObjectMetaData {
        this.meta.fieldsData = this.fieldsData;
        return this.meta;
    }
}


export interface MxFormConfig {
    addIdToUrl?: boolean;
    className?: string;
    closeConfirmation?: boolean;
    force?: boolean;
    formName?: string;
    fromRoute?: boolean;
    hasAsyncValidation?: boolean;
    id?: string;
    multipleColumns?: boolean;
    navigateBackToRouteParent?: boolean;
    notificationMsg?: (o: any) => string;
    onCustomize?: (event: MxFormCustomizerEvent) => {};
    permission?: string;
    properties?: any;
    readonly?: boolean;
    redirectionMsg?: boolean;
    restLink?: string;
    routeLink?: string;
    samePermissionForEdit?: boolean;
    title?: string;
}

export class MxFormCustomizerEvent {
    private _fieldsData$$: BehaviorSubject<FormMetaAndInitialData> = new BehaviorSubject<FormMetaAndInitialData>(null);
    private _customizers: Array<OnBoFormMetadataReady | OnBoFormReady | OnBoFormBeforeSubmit> = [];
    private _defaultActions: Array<Partial<FormActionsConfig>> = [];
    private _props: Array<FormPropsConfig> = [];
    private _boFormConfigs: Array<MxFormConfig> = [];
    private _tabsChanges: UnaryFunction<Observable<FormTabsConfig[]>, Observable<FormTabsConfig[]>>[] = [];
    private _formMode$$: BehaviorSubject<MxFormMode> = new BehaviorSubject<MxFormMode>(null);
    private _copyPasteConfig: Array<CopyPasteConfig> = [];
    private _fieldConfigs: Map<string, MxFieldConfig> = new Map();
    private _pageActions: Array<PageActions>;
    private _fieldsDataConfig: FieldsDataConfig = new FieldsDataConfig();
    private _customActions: Array<MxButtonModel> = [];
    private _closeConfirmationFn: () => Observable<boolean>;

    addCustomizer(customizer: OnBoFormMetadataReady | OnBoFormReady | OnBoFormBeforeSubmit): this {
        this._customizers.push(customizer);
        return this;
    }

    get customizers(): Array<OnBoFormMetadataReady | OnBoFormReady | OnBoFormBeforeSubmit> {
        return this._customizers;
    }

    get boFormBeforeSubmitCustomizers(): Array<OnBoFormBeforeSubmit> {
        return this._customizers
            .filter(c => (<OnBoFormBeforeSubmit>c).boOnBeforeSubmit)
            .map(c => <OnBoFormBeforeSubmit>c);
    }

    get fieldsData(): BehaviorSubject<FormMetaAndInitialData> {
        return this._fieldsData$$;
    }

    get tabsChanges(): UnaryFunction<Observable<FormTabsConfig[]>, Observable<FormTabsConfig[]>>[] {
        return this._tabsChanges;
    }

    get formMode(): BehaviorSubject<MxFormMode> {
        return this._formMode$$;
    }

    get fieldConfigs(): Map<string, MxFieldConfig> {
        return this._fieldConfigs;
    }

    get pageActions(): Array<PageActions> {
        return this._pageActions;
    }

    get customActions(): Array<MxButtonModel> {
        return this._customActions;
    }


    get fieldsDataConfig(): FieldsDataConfig {
        return this._fieldsDataConfig;
    }

    /** Define which class from contract is used in the form and specify its metadata and initial value sources */
    customizeFieldsDataConfig(fieldsDataConfig: FieldsDataConfig): this {
        this._fieldsDataConfig = fieldsDataConfig;
        return this;
    }

    /** Customize form tabs (for example to hide/show tabs based on some conditions) */
    customizeTabsChanges(tabsChanges:  UnaryFunction<Observable<FormTabsConfig[]>, Observable<FormTabsConfig[]>>): void {
        this._tabsChanges.push(tabsChanges);
    }

    /** Customize default forms actions such as 'submit' and 'cancel'
        by changing their text, visibility or disable function */
    customizeActions(actionConfig: Partial<FormActionsConfig> ): this {
        this._defaultActions.push(actionConfig);
        return this;
    }

    /** Add custom buttons to the form */
    customizeCustomActions(actions: Array<MxButtonModel> ): this {
        this._customActions.push(...actions);
        return this;
    }

    /** Customize form behavior, such as navigation, submission handling, error displaying,
      * and additional settings (see FormPropsConfig) */
    customizeProps(props: Partial<FormPropsConfig> ): this {
        this._props.push(props);
        return this;
    }

    /** Define form mode such as 'new', 'edit', 'readonly', 'duplicate' */
    customizeFormMode(formMode: MxFormMode ): this {
        this._formMode$$.next(formMode);
        return this;
    }

    /** Customize general props of the form: permission settings, rest and route links, form name, title, etc. */
    customizeBoFormConfig(props: Partial<MxFormConfig> ): this {
        this._boFormConfigs.push(props);
        return this;
    }

    /** Define classType and copy-paste config to use it for copy-paste operations */
    customizeCopyPasteConfig(config: CopyPasteConfig): this {
        this._copyPasteConfig.push(config);
        return this;
    }

    /** Customize specific field by changing its styling, display options, icon etc. */
    customizeFieldConfigs(fieldName: string, fieldConfig: MxFieldConfig): this {
        this._fieldConfigs.set(fieldName, fieldConfig);
        return this;
    }

    /** Add custom buttons located in the form's header */
    customizePageActions(actions: Array<PageActions>): void {
        this._pageActions = actions;
    }

    getActionsConfig(defaultConf: FormActionsConfig): FormActionsConfig {
        return <FormActionsConfig>this._defaultActions.reduce((acc, curr) => Object.assign(acc, curr), {...defaultConf});
    }

    getProps(): FormPropsConfig {
        return <FormPropsConfig>this._props.reduce((acc, curr) => Object.assign(acc, curr), {});
    }

    getBoFormConfig(): MxFormConfig {
        return <MxFormConfig>this._boFormConfigs.reduce((acc, curr) => Object.assign(acc, curr), {});
    }

    getCopyPasteConfig(): CopyPasteConfig {
        return this._copyPasteConfig.length > 0
            ? <CopyPasteConfig>this._copyPasteConfig.reduce((acc, curr) => Object.assign(acc, curr), {})
            : null;
    }

    /** Customize close confirmation behavior */
    customizeCloseConfirmationFunction(closeConfirmation: () => Observable<boolean>): this {
        this._closeConfirmationFn = closeConfirmation;
        return this;
    }

    get closeConfirmationFunction(): () => Observable<boolean> {
        return this._closeConfirmationFn;
    }
}

@Injectable()
export class MxFormFieldsBuilderService implements OnDestroy {
    private _boFormAndFieldsObj$$: BehaviorSubject<MxFormAndFieldsObj>;
    private _destroy$$: Subject<null> = new Subject<null>();

    constructor(private formService: MxDynamicFormService) {
        this._boFormAndFieldsObj$$ = new BehaviorSubject<MxFormAndFieldsObj>(new MxFormAndFieldsObj(
            new FormGroup({}), []));
    }

    ngOnDestroy(): void {
        this._boFormAndFieldsObj$$.complete();
        this._destroy$$.next(null);
    }

    getForm(): Observable<FormGroup> {
        return this._boFormAndFieldsObj$$.pipe(map(o => o.form));
    }

    getFields(): Observable<Array<FieldUiDataObj>> {
        return this._boFormAndFieldsObj$$.pipe(
            map((o: MxFormAndFieldsObj) => o.fields)
        );
    }

    buildForm(formMode: MxFormMode, boFormCustomizerEvent: MxFormCustomizerEvent): void {
        this.buildSources(boFormCustomizerEvent);
        boFormCustomizerEvent.fieldsData
            .pipe(
                takeUntil(this._destroy$$),
                filter(fieldsData => !!fieldsData && !!fieldsData.objectMetaSource)
            )
            .subscribe({
                next: (fieldsData: FormMetaAndInitialData) => {
                    let fields: Array<FieldUiDataObj> = this.handleObjectMeta(fieldsData, boFormCustomizerEvent);
                    this.initFormWithValueAndSend(
                        boFormCustomizerEvent,
                        this.initForm(formMode, fields),
                        fields,
                        fieldsData.initialData);

                },
                error: (error) => this._boFormAndFieldsObj$$.error(error),
                complete: () => this._boFormAndFieldsObj$$.complete()
            });
    }

    private handleObjectMeta(objectMetaAndInitialData: FormMetaAndInitialData,
                             boFormCustomizerEvent: MxFormCustomizerEvent): Array<FieldUiDataObj> {
        let objectMeta = objectMetaAndInitialData.objectMetaSource;
        let customizedObjectMeta: ObjectMetaData = {...objectMeta, fieldsData: [...objectMeta.fieldsData]};
        boFormCustomizerEvent.customizers.forEach(c => {
            if ((<OnBoFormMetadataReady>c).boOnMetadataReady) {
                customizedObjectMeta = (<OnBoFormMetadataReady>c).boOnMetadataReady(objectMeta);
            }
        });
        return [...customizedObjectMeta.fieldsData];
    }

    private initForm(formMode: MxFormMode, fields: Array<FieldUiDataObj>): FormGroup {
        let form: FormGroup;
        let oldBoFormAndFieldsObj = this._boFormAndFieldsObj$$.value;
        if (!this.compareFields(oldBoFormAndFieldsObj.fields, fields)) {
            form = this.formService.buildForm(fields, formMode, this._destroy$$);
        } else {
            form = oldBoFormAndFieldsObj.form;
        }
        return form;
    }

    private compareFields(oldFields: Array<FieldUiDataObj>, newFields: Array<FieldUiDataObj>): boolean {
        return oldFields.length === newFields.length
            && oldFields.every((value, index) => value === newFields[index]);

    }

    private initFormWithValueAndSend(boFormCustomizerEvent: MxFormCustomizerEvent, form: FormGroup,
                                     fields: Array<FieldUiDataObj>, initialData: any): void {

        this.formService.fillForm(form, fields, initialData);
        // Check if there is a need to do something extra with the form
        boFormCustomizerEvent.customizers.forEach(c => {
            if ((<OnBoFormReady>c).boOnFormReady) {
                (<OnBoFormReady>c).boOnFormReady(form);
            }
        });
        this._boFormAndFieldsObj$$.next(new MxFormAndFieldsObj(form, fields));
    }

    buildSources(event: MxFormCustomizerEvent): void {
        if (!event.fieldsData.getValue() && event.fieldsDataConfig) {
            let metaSource: Observable<ObjectMetaData>;
            if (event.fieldsDataConfig.metaObservable) {
                metaSource = event.fieldsDataConfig.metaObservable;
            } else if (event.fieldsDataConfig.metaObject) {
                metaSource = of(event.fieldsDataConfig.metaObject);
            } else if (event.fieldsDataConfig.className) {
                metaSource = this.formService.getUIData(event.fieldsDataConfig.className,
                    event.fieldsDataConfig.useUiObjectCache,
                    event.fieldsDataConfig.force);
            }

            let valueSource: Observable<any>;
            if (event.fieldsDataConfig.valueObservable) {
                valueSource = event.fieldsDataConfig.valueObservable;
            } else if (event.fieldsDataConfig.valueObject) {
                valueSource = of(event.fieldsDataConfig.valueObject);
            } else if (event.fieldsDataConfig.className
                || event.fieldsDataConfig.metaObservable
                || event.fieldsDataConfig.metaObject) {
                valueSource = of({});
            }
            if (!metaSource || ! valueSource) {
                return;
            }
            combineLatest([metaSource, valueSource])
                .pipe(
                    takeUntil(this._destroy$$)
                )
                .subscribe(([fieldsSource, initialData]: [UiDataObj, any]) => {
                    event.fieldsData.next({
                        objectMetaSource: fieldsSource,
                        initialData: initialData
                    });
                });
        }
    }
}
